// Kinetic first-sentence reveal.
//
// The first sentence of each chapter (the .initial paragraph's first
// sentence, terminated by a period or em-dash) reveals word-by-word on
// first viewport intersection. Subsequent sentences reveal as a block.
//
// Implementation: walk the first text node, split words, wrap each in a
// span with staggered animation-delay. Tag the paragraph with
// .kinetic-ready so CSS can take over.

const STAGGER_MS = 60;
const SENTENCE_END = /(\.|—|\?|!)/;

export function initKineticReveal(): void {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;

  const initials = document.querySelectorAll<HTMLParagraphElement>('section[data-chapter] p.initial');
  initials.forEach((p) => splitFirstSentence(p));

  // Only trigger animation when the paragraph enters the viewport.
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          (e.target as HTMLElement).classList.add('kinetic-go');
          io.unobserve(e.target);
        }
      });
    },
    { threshold: 0.2 }
  );
  initials.forEach((p) => io.observe(p));
}

function splitFirstSentence(p: HTMLParagraphElement): void {
  // Find the first text node.
  const firstNode = p.firstChild;
  if (!firstNode || firstNode.nodeType !== Node.TEXT_NODE) return;
  const text = firstNode.textContent ?? '';
  if (!text.trim()) return;

  // Split off the first sentence.
  const match = text.match(SENTENCE_END);
  if (!match || match.index === undefined) return;
  const splitAt = match.index + 1;
  const firstSentence = text.slice(0, splitAt);
  const remainder = text.slice(splitAt);

  // Build word-wrapped fragment for the first sentence.
  const words = firstSentence.split(/(\s+)/);
  const frag = document.createDocumentFragment();
  let idx = 0;
  for (const w of words) {
    if (/^\s+$/.test(w)) {
      frag.appendChild(document.createTextNode(w));
    } else if (w.length > 0) {
      const span = document.createElement('span');
      span.className = 'kinetic-word';
      span.style.animationDelay = idx * STAGGER_MS + 'ms';
      span.textContent = w;
      frag.appendChild(span);
      idx++;
    }
  }
  // Tail text (rest of the paragraph) stays as-is — no per-word wrap.
  if (remainder) frag.appendChild(document.createTextNode(remainder));

  p.replaceChild(frag, firstNode);
  p.classList.add('kinetic-ready');
}
